import React, { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { supabase } from '../../../services/supabaseClient';
import AdminLayout from '../../../components/admin/AdminLayout';

const DEFAULT_SECTION = {
  title: '',
  content: '',
  position: 0,
  clause_refs: []
};

const AgreementTemplatesPage = () => {
  const [loading, setLoading] = useState(true);
  const [templates, setTemplates] = useState([]);
  const [selectedTemplateId, setSelectedTemplateId] = useState(null);
  const [sections, setSections] = useState([]);
  const [clauses, setClauses] = useState([]);
  const [creatingTemplate, setCreatingTemplate] = useState(false);
  const [newTemplate, setNewTemplate] = useState({ title: '', description: '' });
  const [newSection, setNewSection] = useState({ ...DEFAULT_SECTION });
  const [savingSection, setSavingSection] = useState(false);
  const [savingTemplate, setSavingTemplate] = useState(false);
  const [clauseForm, setClauseForm] = useState({ clause_key: '', title: '', category: '', content: '' });
  const [savingClause, setSavingClause] = useState(false);
  const [error, setError] = useState(null);

  const selectedTemplate = useMemo(
    () => templates.find((tpl) => tpl.id === selectedTemplateId) || null,
    [templates, selectedTemplateId]
  );

  const loadData = async () => {
    try {
      setLoading(true);
      const [{ data: tpl }, { data: cls }] = await Promise.all([
        supabase.from('agreement_templates').select('*').order('created_at', { ascending: false }),
        supabase.from('clause_library').select('*').order('updated_at', { ascending: false })
      ]);
      setTemplates(tpl || []);
      setClauses(cls || []);
      if (!selectedTemplateId && tpl?.length) {
        setSelectedTemplateId(tpl[0].id);
      }
    } catch (err) {
      console.error('Agreement templates load error', err);
      setError('Failed to load templates. Please refresh.');
    } finally {
      setLoading(false);
    }
  };

  const loadSections = async (templateId) => {
    if (!templateId) {
      setSections([]);
      return;
    }
    try {
      const { data, error } = await supabase
        .from('agreement_template_sections')
        .select('*')
        .eq('template_id', templateId)
        .order('position');
      if (error) throw error;
      setSections(data || []);
    } catch (err) {
      console.error('Template sections load error', err);
      setError('Failed to load sections for template.');
    }
  };

  useEffect(() => {
    loadData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (selectedTemplateId) {
      loadSections(selectedTemplateId);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedTemplateId]);

  const handleCreateTemplate = async (event) => {
    event.preventDefault();
    if (!newTemplate.title.trim()) {
      setError('Template title is required.');
      return;
    }
    try {
      setSavingTemplate(true);
      const payload = {
        title: newTemplate.title.trim(),
        description: newTemplate.description.trim() || null,
        status: 'draft'
      };
      const { data, error: insertError } = await supabase
        .from('agreement_templates')
        .insert([payload])
        .select()
        .single();
      if (insertError) throw insertError;
      setNewTemplate({ title: '', description: '' });
      setCreatingTemplate(false);
      await loadData();
      if (data?.id) {
        setSelectedTemplateId(data.id);
      }
    } catch (err) {
      console.error('Create template error', err);
      setError(err.message || 'Failed to create template.');
    } finally {
      setSavingTemplate(false);
    }
  };

  const handleAddSection = async () => {
    if (!selectedTemplateId) {
      setError('Select a template first.');
      return;
    }
    if (!newSection.content.trim()) {
      setError('Section content cannot be empty.');
      return;
    }
    try {
      setSavingSection(true);
      const payload = {
        template_id: selectedTemplateId,
        title: newSection.title.trim() || null,
        content: newSection.content,
        position: Number(newSection.position) || sections.length,
        clause_refs: newSection.clause_refs || []
      };
      const { error: insertError } = await supabase.from('agreement_template_sections').insert([payload]);
      if (insertError) throw insertError;
      setNewSection({ ...DEFAULT_SECTION });
      await loadSections(selectedTemplateId);
    } catch (err) {
      console.error('Add section error', err);
      setError(err.message || 'Failed to add section.');
    } finally {
      setSavingSection(false);
    }
  };

  const handleDeleteSection = async (sectionId) => {
    if (!sectionId) return;
    if (typeof window !== 'undefined' && !window.confirm('Remove this section?')) return;
    try {
      const { error: deleteError } = await supabase
        .from('agreement_template_sections')
        .delete()
        .eq('id', sectionId);
      if (deleteError) throw deleteError;
      await loadSections(selectedTemplateId);
    } catch (err) {
      console.error('Delete section error', err);
      setError('Failed to remove section.');
    }
  };

  const handleCreateClause = async (event) => {
    event.preventDefault();
    if (!clauseForm.clause_key.trim() || !clauseForm.title.trim() || !clauseForm.content.trim()) {
      setError('Clause key, title, and content are required.');
      return;
    }
    try {
      setSavingClause(true);
      const payload = {
        clause_key: clauseForm.clause_key.trim(),
        title: clauseForm.title.trim(),
        category: clauseForm.category.trim() || null,
        content: clauseForm.content,
        version: 1,
        is_active: true
      };
      const { error: insertError } = await supabase.from('clause_library').insert([payload]);
      if (insertError) throw insertError;
      setClauseForm({ clause_key: '', title: '', category: '', content: '' });
      await loadData();
    } catch (err) {
      console.error('Create clause error', err);
      setError(err.message || 'Failed to create clause.');
    } finally {
      setSavingClause(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-900 font-mono text-white p-6">
        <div className="flex justify-center items-center h-64">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-500"></div>
        </div>
      </div>
    );
  }

  return (
    <AdminLayout>
      <div className="min-h-screen bg-gray-900 font-mono text-white p-6 space-y-6">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold text-white">Agreement Templates & Clauses</h1>
            <p className="text-sm text-gray-400">
              Build reusable templates and clause snippets to speed up drafting and standardize language.
            </p>
          </div>
          <Link to="/admin/agreements" className="px-3 py-2 text-sm border border-gray-600 rounded text-gray-300 hover:bg-gray-800 transition-colors">
            Back to Agreements
          </Link>
        </div>

        {error && (
          <div className="bg-red-900/20 border-l-4 border-red-500 text-red-400 px-4 py-3 rounded">
            <p className="text-sm">{error}</p>
          </div>
        )}

        <div className="bg-gray-800 border border-gray-700 rounded-lg p-4 space-y-4 shadow">
          <div className="flex items-center justify-between">
            <h2 className="text-lg font-medium text-white">Templates</h2>
            <button
              onClick={() => setCreatingTemplate((v) => !v)}
              className="px-3 py-1 text-xs border border-gray-600 rounded bg-gray-700 text-white hover:bg-gray-600 transition-colors"
            >
              {creatingTemplate ? 'Cancel' : 'New Template'}
            </button>
          </div>
          {creatingTemplate && (
            <form onSubmit={handleCreateTemplate} className="grid md:grid-cols-2 gap-3 text-sm">
              <input
                className="border border-gray-600 bg-gray-700 text-white rounded px-3 py-2 placeholder-gray-500 focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                placeholder="Template title"
                value={newTemplate.title}
                onChange={(e) => setNewTemplate((prev) => ({ ...prev, title: e.target.value }))}
              />
              <input
                className="border border-gray-600 bg-gray-700 text-white rounded px-3 py-2 placeholder-gray-500 focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                placeholder="Description (optional)"
                value={newTemplate.description}
                onChange={(e) => setNewTemplate((prev) => ({ ...prev, description: e.target.value }))}
              />
              <div className="md:col-span-2 flex items-center gap-2">
                <button
                  type="submit"
                  disabled={savingTemplate}
                  className="px-3 py-2 text-xs bg-blue-600 text-white rounded hover:bg-blue-700 disabled:opacity-60 transition-colors"
                >
                  {savingTemplate ? 'Saving…' : 'Create Template'}
                </button>
              </div>
            </form>
          )}

          <div className="grid md:grid-cols-3 gap-3 text-sm">
            {templates.map((tpl) => (
              <button
                key={tpl.id}
                onClick={() => setSelectedTemplateId(tpl.id)}
                className={`px-3 py-2 text-left border rounded text-sm transition-colors ${tpl.id === selectedTemplateId ? 'border-blue-500 bg-blue-900/20' : 'border-gray-600 bg-gray-700 hover:bg-gray-600'
                  }`}
              >
                <div className="font-medium text-white">{tpl.title}</div>
                <div className="text-xs text-gray-400">{tpl.status}</div>
                <div className="text-xs text-gray-500">
                  Updated {tpl.updated_at ? new Date(tpl.updated_at).toLocaleString() : '-'}
                </div>
              </button>
            ))}
            {templates.length === 0 && (
              <div className="px-3 py-2 text-sm text-gray-500 border border-gray-600 rounded bg-gray-700">
                No templates yet. Create your first template to get started.
              </div>
            )}
          </div>
        </div>

        {selectedTemplate && (
          <div className="bg-gray-800 border border-gray-700 rounded-lg p-4 space-y-4 shadow">
            <div className="flex items-center justify-between">
              <h2 className="text-lg font-medium text-white">
                Sections — {selectedTemplate.title}
              </h2>
              <div className="text-xs text-gray-400">
                {sections.length} section{sections.length === 1 ? '' : 's'}
              </div>
            </div>

            <div className="grid md:grid-cols-4 gap-3 text-sm">
              <input
                className="border border-gray-600 bg-gray-700 text-white rounded px-3 py-2 md:col-span-2 placeholder-gray-500 focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                placeholder="Section title (optional)"
                value={newSection.title}
                onChange={(e) => setNewSection((prev) => ({ ...prev, title: e.target.value }))}
              />
              <input
                className="border border-gray-600 bg-gray-700 text-white rounded px-3 py-2 placeholder-gray-500 focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                placeholder="Order (0-based)"
                value={newSection.position}
                onChange={(e) => setNewSection((prev) => ({ ...prev, position: e.target.value }))}
              />
              <input
                className="border border-gray-600 bg-gray-700 text-white rounded px-3 py-2 placeholder-gray-500 focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                placeholder="Clause keys (comma separated)"
                value={(newSection.clause_refs || []).join(',')}
                onChange={(e) =>
                  setNewSection((prev) => ({
                    ...prev,
                    clause_refs: e.target.value.split(',').map((s) => s.trim()).filter(Boolean)
                  }))
                }
              />
              <textarea
                className="border border-gray-600 bg-gray-700 text-white rounded px-3 py-2 md:col-span-4 h-32 placeholder-gray-500 focus:ring-2 focus:ring-blue-500 focus:border-transparent font-mono text-xs"
                placeholder="Section content (supports template variables like {{client_name}})"
                value={newSection.content}
                onChange={(e) => setNewSection((prev) => ({ ...prev, content: e.target.value }))}
              />
              <div className="md:col-span-4">
                <button
                  onClick={handleAddSection}
                  disabled={savingSection}
                  className="px-3 py-2 text-xs bg-blue-600 text-white rounded hover:bg-blue-700 disabled:opacity-60 transition-colors"
                >
                  {savingSection ? 'Adding…' : 'Add Section'}
                </button>
              </div>
            </div>

            <div className="space-y-3">
              {sections.map((section) => (
                <div key={section.id} className="border border-gray-600 bg-gray-700/50 rounded px-4 py-3 text-sm space-y-2">
                  <div className="flex items-center justify-between">
                    <div>
                      <div className="text-xs uppercase tracking-wide text-gray-500">
                        Position {section.position}
                      </div>
                      <div className="text-sm font-medium text-white">
                        {section.title || 'Untitled section'}
                      </div>
                    </div>
                    <button
                      onClick={() => handleDeleteSection(section.id)}
                      className="px-2 py-1 text-xs border rounded text-red-400 border-red-800 bg-red-900/20 hover:bg-red-900/40 transition-colors"
                    >
                      Remove
                    </button>
                  </div>
                  {section.clause_refs?.length > 0 && (
                    <div className="text-xs text-gray-400">
                      Clauses: {section.clause_refs.join(', ')}
                    </div>
                  )}
                  <pre className="text-xs bg-gray-900 border border-gray-700 text-gray-300 rounded px-3 py-2 overflow-x-auto font-mono">
                    {section.content}
                  </pre>
                </div>
              ))}
              {sections.length === 0 && (
                <div className="text-sm text-gray-500">No sections yet. Add your first section above.</div>
              )}
            </div>
          </div>
        )}

        <div className="bg-gray-800 border border-gray-700 rounded-lg p-4 space-y-4 shadow">
          <div className="flex items-center justify-between">
            <h2 className="text-lg font-medium text-white">Clause Library</h2>
            <span className="text-xs text-gray-400">{clauses.length} clauses</span>
          </div>

          <form onSubmit={handleCreateClause} className="grid md:grid-cols-2 gap-3 text-sm">
            <input
              className="border border-gray-600 bg-gray-700 text-white rounded px-3 py-2 placeholder-gray-500 focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              placeholder="Clause key (unique identifier)"
              value={clauseForm.clause_key}
              onChange={(e) => setClauseForm((prev) => ({ ...prev, clause_key: e.target.value }))}
            />
            <input
              className="border border-gray-600 bg-gray-700 text-white rounded px-3 py-2 placeholder-gray-500 focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              placeholder="Clause title"
              value={clauseForm.title}
              onChange={(e) => setClauseForm((prev) => ({ ...prev, title: e.target.value }))}
            />
            <input
              className="border border-gray-600 bg-gray-700 text-white rounded px-3 py-2 placeholder-gray-500 focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              placeholder="Category (optional)"
              value={clauseForm.category}
              onChange={(e) => setClauseForm((prev) => ({ ...prev, category: e.target.value }))}
            />
            <textarea
              className="border border-gray-600 bg-gray-700 text-white rounded px-3 py-2 md:col-span-2 h-24 placeholder-gray-500 focus:ring-2 focus:ring-blue-500 focus:border-transparent font-mono text-xs"
              placeholder="Clause content"
              value={clauseForm.content}
              onChange={(e) => setClauseForm((prev) => ({ ...prev, content: e.target.value }))}
            />
            <div className="md:col-span-2">
              <button
                type="submit"
                disabled={savingClause}
                className="px-3 py-2 text-xs bg-blue-600 text-white rounded hover:bg-blue-700 disabled:opacity-60 transition-colors"
              >
                {savingClause ? 'Saving clause…' : 'Add Clause'}
              </button>
            </div>
          </form>

          <div className="divide-y divide-gray-700">
            {clauses.map((clause) => (
              <div key={clause.id} className="py-3 text-sm">
                <div className="flex items-center justify-between">
                  <div>
                    <div className="font-medium text-white">{clause.title}</div>
                    <div className="text-xs text-gray-400">
                      Key: <span className="font-mono text-blue-400">{clause.clause_key}</span> • Version {clause.version} • {clause.is_active ? 'Active' : 'Inactive'}
                    </div>
                  </div>
                </div>
                {clause.category && (
                  <div className="text-xs text-gray-500 mt-1">Category: {clause.category}</div>
                )}
                <pre className="text-xs bg-gray-900 border border-gray-700 text-gray-300 rounded px-3 py-2 mt-2 overflow-x-auto font-mono">
                  {clause.content}
                </pre>
              </div>
            ))}
            {clauses.length === 0 && (
              <div className="py-3 text-sm text-gray-500">
                Clause library is empty. Add clauses to reuse them across templates.
              </div>
            )}
          </div>
        </div>
      </div>
    </AdminLayout>
  );
};

export default AgreementTemplatesPage;
